<template>
  <div :class="`${rowClass} h-100`">
    <div :class="`${colClass} h-100`">
      <div class="h-100">
        <page-calendar-legend
          v-if="legend.length > 0 && legendPosition == 'top'"
          :organization="organization"
          :legend="legend"
          class="my-2"
        ></page-calendar-legend>
        <b-row class="mb-2">
          <b-col class="col-auto pr-0">
            <b-button-group size="sm">
              <b-button variant="outline-branded" size="sm" :disabled="busy" @click="onClickPrevious">
                <i class="fa fa-fw fa-chevron-left"></i>
              </b-button>
              <b-button variant="outline-branded" size="sm" :disabled="busy" @click="onClickNext">
                <i class="fa fa-fw fa-chevron-right"></i>
              </b-button>
            </b-button-group>
          </b-col>
          <b-col class="">
            <date-picker v-model="date" :config="dateConfig" :disabled="busy">
              <span class="lead d-none d-lg-inline-block">{{ dateRange }}</span>
              <span class="d-none d-md-inline-block d-lg-none pt-1">{{ dateRange }}</span>
              <small class="d-inline-block d-md-none pt-1">{{ dateRange }}</small>
              <i class="fa fa-fw fa-caret-down"></i>
            </date-picker>
          </b-col>
          <b-col class="col-auto pl-0">
            <b-button-group size="sm">
              <b-button v-if="views.indexOf('dayGridMonth') >= 0"
                        :variant="view == 'dayGridMonth' ? 'branded' : 'outline-branded'"
                        @click="onChangeView('dayGridMonth')" :disabled="busy">
                {{ trans('month') }}
              </b-button>
              <b-button v-if="views.indexOf('timeGridWeek') >= 0"
                        :variant="view == 'timeGridWeek' ? 'branded' : 'outline-branded'"
                        @click="onChangeView('timeGridWeek')" :disabled="busy">
                {{ trans('week') }}
              </b-button>
              <b-button v-if="views.indexOf('timeGridDay') >= 0"
                        :variant="view == 'timeGridDay' ? 'branded' : 'outline-branded'"
                        @click="onChangeView('timeGridDay')" :disabled="busy">
                {{ trans('day') }}
              </b-button>
            </b-button-group>
          </b-col>
        </b-row>

        <b-overlay :show="busy">
          <full-calendar
            ref="calendar"
            :defaultDate="date"
            :defaultView="defaultView"
            :views="views"
            :firstDay="1"
            height="auto"
            :plugins="plugins"
            :locale="locale"
            themeSystem="bootstrap"
            :minTime="minTime || '00:00:00'"
            :maxTime="maxTime == '00:00:00' || !maxTime ? '24:00:00' : maxTime"
            :header="false"
            :eventSources="eventSources"
            :eventRender="onEventRender"
            :datesRender="onDatesRender"
            @eventClick="onEventClick"
            :selectable="isPreview"
            @select="onSelect"
            @unselect="onUnselect"
          ></full-calendar>
          <b-row class="m-0 justify-content-between">
            <b-button v-if="export_calendar" variant="link" size="sm" class="px-0 mr-100" @click="onICalendarClick()">
              <i class="fa fa-fw fa-calendar"></i>
              {{ trans('i_calendar') }}
            </b-button>
            <page-calendar-legend
              v-if="legend.length > 0 && legendPosition == 'bottom'"
              :organization="organization"
              :legend="legend"
              class="my-2"
            ></page-calendar-legend>
          </b-row>
        </b-overlay>
      </div>
    </div>
    <page-calendar-event-modal
      :modal-id="'page-calendar-event-modal_'+block.id"
      :organization="organization"
      :page="page"
      :block="block"
      :event="event"
      :is-preview="isPreview"
      @updated="onRefresh"
    ></page-calendar-event-modal>
    <page-i-calendar-modal
      v-if="!isPreview"
      :organization="organization"
      :block="block"
      :page="page"
    ></page-i-calendar-modal>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import bootstrapPlugin from '@fullcalendar/bootstrap'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr-ca'
import '@fullcalendar/core/main.css'
import '@fullcalendar/daygrid/main.css'
import '@fullcalendar/timegrid/main.css'
import DatePicker from './../../../common/components/DatePicker'
import PageCalendarEventModal from './PageCalendarEventModal'
import PageCalendarLegend from './PageCalendarLegend'
import PageICalendarModal from './PageICalendarModal'

export default {
  props: {
    organization: Object,
    page: Object,
    block: Object,
    isPreview: {
      type: Boolean,
      default: false,
    },
    showParticipants: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    FullCalendar,
    DatePicker,
    PageCalendarEventModal,
    PageCalendarLegend,
    PageICalendarModal,
  },
  data() {
    return {
      trans: trans,
      busy: false,
      error: null,
      iframeUrlPrefix: _.iframeUrlPrefix,
      locale: window.lang == 'fr' ? frLocale : undefined,
      plugins: [dayGridPlugin, timeGridPlugin, bootstrapPlugin, interactionPlugin],
      date: moment(this.block.meta.date || undefined).format('YYYY-MM-DD'),
      dateConfig: {
        locale: window.lang,
        format: 'YYYY-MM-DD',
        showTodayButton: true,
      },
      start: null,
      end: null,
      show_participants: this.block?.meta?.showParticipants ?? false,
      view: this.block.meta.view,
      event: null,
      eventSources: [
        {events: this.fetchEventSources},
      ],
    }
  },
  computed: {
    rowClass() {
      return 'row'
    },
    colClass() {
      return 'col-12'
    },
    title() {
      return this.block.text
    },
    legend() {
      return this.block.meta.legend || []
    },
    legendPosition() {
      return this.block.meta.legend_position || 'bottom'
    },
    defaultDate() {
      return moment(this.block.meta.date || undefined).format('YYYY-MM-DD')
    },
    minTime() {
      return this.block.meta.minTime
    },
    maxTime() {
      return this.block.meta.maxTime
    },
    defaultView() {
      if (this.is_mobile && this.block.meta.view_mobile) return this.block.meta.view_mobile
      return this.block.meta.view
    },
    views() {
      return this.block.meta.views
    },
    dateRange() {
      let str = ''
      if (this.view.indexOf('Week') >= 0 && this.start && this.end) {
        str = _.dateRangeStr(moment(this.start), moment(this.end).add(-1, 'days'))
      } else if (this.view.indexOf('Month') >= 0) {
        str = moment(this.date).format('MMMM Y')
      } else if (this.view.indexOf('Day') >= 0) {
        str = moment(this.date).format('D MMM Y')
      }
      return str
    },
    events() {
      return _.map(this.block.meta.events || [], e => {
        let event = _.assign({type: 'custom'}, e)
        if (event.allDay && event.end) {
          event.end = moment(event.end).add(1, 'day').toISOString()
        }
        return event
      })
    },
    export_calendar() {
      return this.block.meta.export_calendar
    },
    is_mobile() {
      return window.innerWidth <= 420
    }
  },
  watch: {
    block() {
      this.view = this.block.meta.view
      this.show_participants = this.block.meta.show_participants ?? false
      this.date = moment(this.block.meta.date || undefined).format('YYYY-MM-DD')
      if (this.$refs.calendar) {
        this.$refs.calendar.getApi().changeView(this.view)
        this.$refs.calendar.getApi().gotoDate(this.date)
        this.$refs.calendar.getApi().refetchEvents()
      }
    },
    date() {
      if (this.date == moment(this.$refs.calendar.getApi().getDate()).format('YYYY-MM-DD')) return
      if (this.start && this.date == moment(this.start).format('YYYY-MM-DD')) return
      this.$refs.calendar.getApi().gotoDate(this.date)
    },
  },
  methods: {
    onClickNext() {
      this.$refs.calendar.getApi().next()
    },
    onClickPrevious() {
      this.$refs.calendar.getApi().prev()
    },
    removeHiddenEvents(events) {
      return _.reduce(events, (events, e) => {
        if (!e.extendedProps.hidden) events.push(e)
        return events
      }, [])
    },
    onRefresh() {
      this.$refs.calendar.getApi().refetchEvents()
    },
    fetchEventSources(info, success, failure) {
      let events = []
      if (!this.isPreview) {
        let slug = (this.page.slug || '').replace('/', '') || 'undefined'
        let url = `/org/${this.organization.id}/page/${this.page.upid}/${slug}/calendar_events/${this.block.id}?start=${encodeURIComponent(info.startStr)}&end=${encodeURIComponent(info.endStr)}&show_participants=${this.block.meta.show_participants ?? false}`
        axios.get(url)
          .then(response => {
            this.busy = false
            success(_.union(this.events, this.removeHiddenEvents(response.data)))
          })
          .catch(error => {
            this.busy = false
            this.error = _.errorFromAjax(error)
            failure(this.error)
          })
      } else {
        let url = `/organization/${this.organization.id}/page/${this.page.id}/simulate_calendar_events?start=${encodeURIComponent(info.startStr)}&end=${encodeURIComponent(info.endStr)}`
        axios.post(url, this.block)
          .then(response => {
            this.busy = false
            success(_.union(this.events, this.removeHiddenEvents(response.data)))
          })
          .catch(error => {
            this.busy = false
            this.error = _.errorFromAjax(error)
            failure(this.error)
          })
      }
    },
    onChangeView(view) {
      this.view = view
      this.$refs.calendar.getApi().changeView(this.view)
    },
    onDatesRender() {
      this.date = moment(this.$refs.calendar.getApi().getDate()).format('YYYY-MM-DD')
      this.start = this.$refs.calendar.getApi().view.activeStart
      this.end = this.$refs.calendar.getApi().view.activeEnd
    },
    onEventRender(info) {
      let subtitle = info.event.extendedProps.subtitle ? `<div class="font-weight-normal">${info.event.extendedProps.subtitle}</div>` : ''
      let title = `<div class="fc-title font-weight-bold">${info.event.title}${subtitle}</div>`
      info.el.querySelector('.fc-title').outerHTML = title
      // Swap the order
      if (info.el.querySelector('.fc-time'))
        info.el.querySelector('.fc-content').insertBefore(info.el.querySelector('.fc-title'), info.el.querySelector('.fc-time'))
      if (!info.el.href) info.el.href = 'javascript:void(0);' // Fix for https://github.com/fullcalendar/fullcalendar/issues/3364
    },
    onEventClick(e) {
      e.el.focus() // Fix for https://github.com/fullcalendar/fullcalendar/issues/3364
      if (!e.url) e.jsEvent.preventDefault()
      let event = e.event
      if (this.isPreview && event.extendedProps.type == 'custom') {
        this.$emit('click', event)
      } else if (event.extendedProps.type == 'am_class' && !!event.extendedProps.form_url) {
        if (!this.isPreview) {
          window.formIframeModalUpid = _.extractUpidFromUrl(event.extendedProps.form_url)
          this.$bvModal.show('form-iframe-modal')
        }
      } else {
        this.event = event
        this.$bvModal.show(`page-calendar-event-modal_${this.block.id}`)
      }
    },
    onSelect(info) {
      this.$emit('select', info)
    },
    onUnselect(info) {
      this.$emit('unselect', info)
    },
    onICalendarClick() {
      if (!this.isPreview) this.$bvModal.show(`page-i-calendar-modal_${this.block.id}`)
    },
  },
  mounted() {
    window.pageCalendarBlock = this
  },
}
</script>
